import { useMemo, useState } from 'react';
import { faChevronDown, faChevronUp, faClose } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { Icon } from 'framework/Icon';
import { ModalContext, useModalContext } from 'framework/modal/context/ModalContext';

import { EMPTY_FUNCTION, MODAL_ID } from '../../constants';

import { useModalEscape } from './hooks/useModalEscape';
import useModalTheme from './hooks/useModalTheme';
import { ModalElement, ModalFormElement } from './elements';
import { ModalBodyProps, ModalFooterProps, ModalHeaderProps, ModalProps } from './types';

import cn from './Modal.module.scss';

export const Modal = ({
  is_open = false,
  identifier,
  duration = 300,
  width = '500px',
  gap = '16px',
  height = 'max-content',
  onSubmit = EMPTY_FUNCTION,
  onClose = EMPTY_FUNCTION,
  dismiss = false,
  theme,
  children,
}: ModalProps) => {
  useModalEscape(identifier, () => onClose({ [identifier]: is_open }));

  const modal_theme = useModalTheme(theme);

  const context = useMemo(
    () => ({ theme, is_open, identifier, onClose }),
    [theme, is_open, identifier, onClose]
  );

  if (!is_open) {
    return null;
  }

  return (
    <ModalContext.Provider value={context}>
      <ModalElement
        id={MODAL_ID}
        className={cn.backdrop}
        data-identifier={identifier}
        is_visible={is_open}
        theme={modal_theme}
        duration={duration}
        onClick={() => dismiss && onClose({ [identifier]: is_open })}
      >
        <ModalFormElement
          className={cn.modal}
          theme={modal_theme}
          width={width}
          height={height}
          gap={gap}
          onSubmit={onSubmit}
        >
          {children}
        </ModalFormElement>
      </ModalElement>
    </ModalContext.Provider>
  );
};

const Header = ({ children, dismiss = true }: ModalHeaderProps) => {
  const { onClose, identifier, is_open } = useModalContext();

  return (
    <div className={cn.header}>
      <h2 className={cn.title}>{children}</h2>
      {dismiss && (
        <div className={cn.close} onClick={() => onClose({ [identifier]: is_open })}>
          <Icon icon={faClose} size="14px" />
        </div>
      )}
    </div>
  );
};

const Body = ({ children, gap = '16px', padding = '16px 0px 0px 0px' }: ModalBodyProps) => {
  const { theme } = useModalContext();
  const modal_theme = useModalTheme(theme);

  return (
    <div className={cn.body} style={{ gap, padding, borderTop: modal_theme.border.normal }}>
      {children}
    </div>
  );
};

const Additional = ({ children, gap = '16px', padding = '16px 0px 0px 0px' }: ModalBodyProps) => {
  const [open, setOpen] = useState(false);
  const { theme } = useModalContext();
  const modal_theme = useModalTheme(theme);
  return (
    <div>
      <div
        className={classNames(cn.label, 'link', { [cn.margin]: open })}
        onClick={() => setOpen(!open)}
      >
        <Icon icon={open ? faChevronUp : faChevronDown} />
        <p>Additional Fields</p>
      </div>
      {open && (
        <div
          className={cn.additional}
          style={{ gap, padding, borderTop: modal_theme.border.normal }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

Modal.Footer = ({ children, margin = '16px 0px 0px 0px' }: ModalFooterProps) => {
  return (
    <div className={cn.footer} style={{ margin }}>
      {children}
    </div>
  );
};

Modal.Header = Header;
Modal.Body = Body;
Modal.Additional = Additional;
